'use client';
import {
	type AclUserCredentials,
	daysToMs,
	type FetchMeCredentialsData,
	sessionAtom,
	UseCredentialsQueryKeys,
	userApi
} from '@capsicum/common';
import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';

export type UseCredentialsQueryOptions = Omit<
	UseQueryOptions<FetchMeCredentialsData, unknown, AclUserCredentials | undefined>,
	'queryFn' | 'select'
>;

export const useCredentialsQuery = (options?: UseCredentialsQueryOptions) => {
	const sessionTokens = useAtomValue(sessionAtom);

	const { data, error, isError, isFetching, isLoading, refetch } = useQuery<
		FetchMeCredentialsData,
		unknown,
		AclUserCredentials | undefined
	>({
		enabled: !!sessionTokens?.authToken,
		queryFn: async () => (await userApi).fetchMeCredentials(),
		queryKey: UseCredentialsQueryKeys,
		select: res => res.data,
		staleTime: daysToMs(1),
		...options
	});

	const { credentials, forcedRequirementsIdentifiers, user } = data || {};

	return {
		areCredentialsFetching: isFetching,
		areCredentialsLoading: isLoading,
		credentials,
		error,
		forcedRequirementsIdentifiers,
		isCredentialsError: isError,
		refetchCredentials: refetch,
		user
	};
};
